import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design5/icons';
import { Segmented, Space } from 'antd5';
import styled from 'styled-components';

import { Button } from 'components/ui';

import type { SaveStatus } from './persistence/useDashboardPersistence';

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const SaveIndicator = styled.span.attrs({ role: 'status', 'aria-live': 'polite' })`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.labelColor};
`;

type DashboardToolbarProps = {
  isEditing: boolean;
  saveStatus: SaveStatus;
  /** Number of widgets not yet on the dashboard, surfaced on the Add button. */
  addableWidgetCount: number;
  onToggleEdit: (editing: boolean) => void;
  onAddClick: () => void;
  onReset: () => void;
};

// One persistent live region whose CONTENT changes with status. A region that is
// mounted/unmounted per status (or returns null when idle) is announced
// unreliably by screen readers; keeping the node and swapping its text fixes that.
function SaveStatusLabel({ status }: { status: SaveStatus }) {
  return (
    <SaveIndicator>
      {status === 'saving' && (
        <>
          <LoadingOutlined /> Saving…
        </>
      )}
      {status === 'saved' && (
        <>
          <CheckCircleOutlined /> Saved
        </>
      )}
      {status === 'error' && (
        <>
          <ExclamationCircleOutlined /> Couldn’t save
        </>
      )}
    </SaveIndicator>
  );
}

/** View/Edit switch plus the edit-mode actions (Add widget, Reset) and a save indicator. */
export function DashboardToolbar({
  isEditing,
  saveStatus,
  addableWidgetCount,
  onToggleEdit,
  onAddClick,
  onReset,
}: DashboardToolbarProps) {
  return (
    <ToolbarRow>
      {isEditing && <SaveStatusLabel status={saveStatus} />}
      <Space>
        {isEditing && (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddClick}
              data-testid="dashboard-add-widget"
            >
              {addableWidgetCount > 0 ? `Add widget · ${addableWidgetCount}` : 'Add widget'}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={onReset} data-testid="dashboard-reset">
              Reset
            </Button>
          </>
        )}
        <Segmented
          data-testid="dashboard-mode-toggle"
          value={isEditing ? 'edit' : 'view'}
          onChange={value => onToggleEdit(value === 'edit')}
          options={[
            { label: 'View', value: 'view' },
            { label: 'Customize', value: 'edit' },
          ]}
        />
      </Space>
    </ToolbarRow>
  );
}
